import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { isEmpty } from 'validate.js';

import useCurrentUser from 'dpl/common/hooks/useCurrentUser';
import FullPageQuiz, {
  PROGRESS_BAR_VARIANTS
} from 'dpl/components/FullPageQuiz';
import { FULL_PAGE_QUIZ_QUESTION_VARIANTS } from 'dpl/components/FullPageQuiz/FullPageQuizQuestion';
import LoadingWrapper from 'dpl/components/LoadingWrapper';
import OverlayModal from 'dpl/components/OverlayModal';
import useForm from 'dpl/shared/form/hooks/useForm';
import FormContext from 'dpl/shared/form/utils/context';
import { get } from 'dpl/shared/utils/object';
import { CONSTRAINT_TYPES } from 'dpl/shared/validations/constraints';
import {
  BREEDER_ACTIVE_MESSAGES_PATH,
  BUYER_ACTIVE_MESSAGES_PATH
} from 'dpl/shared/constants/urls';

import {
  BUTTON_SEND_REQUEST_LABEL,
  BUTTON_CONTINUE_LABEL,
  FORM_BUYER_POSTAL_CODE,
  QUIZ_QUESTION_BUYER_POSTAL,
  FORM_BUYER_HOME_TYPE,
  QUIZ_QUESTION_BUYER_HOME,
  FORM_BUYER_OUTDOOR_ACCESS,
  QUIZ_QUESTION_BUYER_OUTDOOR,
  FORM_BUYER_LIFESTYLE,
  QUIZ_QUESTION_BUYER_LIFESTYLE,
  FORM_BUYER_PROFILE_PHOTO,
  QUIZ_QUESTION_BUYER_PROFILE_PHOTO,
  FORM_BUYER_HOUSEHOLD,
  QUIZ_QUESTION_BUYER_HOUSEHOLD,
  FORM_BUYER_PET_EXPERIENCE,
  QUIZ_QUESTION_BUYER_PET_EXPERIENCE
} from '../utils/constants';
import BuyerProfileContext from '../utils/buyer_profile_quiz_context';
import useBuyerProfileQuiz from '../hooks/useBuyerProfileQuiz';
import { trackStepAnswer, trackStepView } from '../utils/analytics';
import QuizZipcodeQuestion from './QuizZipCodeQuestion';
import QuizHomeQuestion from './QuizHomeQuestion';
import QuizOutdoorQuestion from './QuizOutdoorQuestion';
import QuizLifestyleQuestion from './QuizLifestyleQuestion';
import QuizProfilePhotoQuestion from './QuizProfilePhotoQuestion';
import QuizHouseholdQuestion from './QuizHouseholdQuestion';
import QuizPetExperienceQuestion from './QuizPetExperienceQuestion';

const WIDE_QUIZ_QUESTION = FULL_PAGE_QUIZ_QUESTION_VARIANTS.WIDE;

function BuyerProfileQuizContent() {
  const {
    isModalOpen,
    closeModal,
    conversationId,
    breederProfile,
    isFetching,
    isExpMiniAppEngagementVariantEnabled
  } = useBuyerProfileQuiz();

  const currentUser = useCurrentUser();

  let orderedSteps = [];

  const profileIds = {
    breeder_profile_id: breederProfile.id,
    buyer_profile_id: get(currentUser, 'value.data.buyer_profile_id')
  };
  const {
    contextValue,
    formState,
    formErrors,
    reset,
    handleFormFieldChange,
    handleSubmission
  } = useForm({
    defaultFormState: {
      buyer_profile: {
        home_type: '',
        outdoor_access: '',
        household_tags: [],
        pet_experience_tags: [],
        address_attributes: {
          postal_code: ''
        },
        user_attributes: {
          profile_photo_attributes: null
        },
        conversation_message: {
          conversation_id: conversationId,
          message_text: ''
        }
      }
    },
    onSubmit: async () => {
      try {
        const lastStep = orderedSteps[orderedSteps.length - 1];

        if (lastStep.inputName) {
          trackStepAnswer({
            step: lastStep,
            formState,
            properties: 'profileIds'
          });
        }

        const conversationUrl = currentUser.isBreeder
          ? `${BREEDER_ACTIVE_MESSAGES_PATH}/${conversationId}`
          : `${BUYER_ACTIVE_MESSAGES_PATH}/${conversationId}`;

        window.location.assign(conversationUrl);
      } catch (error) {
        closeModal();
      }
    },
    mapFormStateToValidationSchema: _formState => {
      return {
        [FORM_BUYER_POSTAL_CODE]:
          get(_formState, FORM_BUYER_POSTAL_CODE) && CONSTRAINT_TYPES.ZIP
      };
    }
  });

  useEffect(() => {
    if (!isFetching && isModalOpen) reset();
  }, [isFetching, isModalOpen]);

  if (!isModalOpen) return null;

  const isLoading = isFetching || contextValue.isSubmitting;

  const commonProps = { icon: null };

  orderedSteps = [
    {
      inputName: FORM_BUYER_POSTAL_CODE,
      questionName: QUIZ_QUESTION_BUYER_POSTAL,
      Component: QuizZipcodeQuestion,
      isValid:
        (!isEmpty(get(formState, FORM_BUYER_POSTAL_CODE)) &&
          isEmpty(formErrors[FORM_BUYER_POSTAL_CODE])) ||
        isEmpty(get(formState, FORM_BUYER_POSTAL_CODE)),
      otherProps: commonProps
    },
    {
      inputName: FORM_BUYER_HOME_TYPE,
      questionName: QUIZ_QUESTION_BUYER_HOME,
      Component: QuizHomeQuestion,
      otherProps: commonProps
    },
    {
      inputName: FORM_BUYER_HOUSEHOLD,
      questionName: QUIZ_QUESTION_BUYER_HOUSEHOLD,
      Component: QuizHouseholdQuestion,
      otherProps: commonProps
    },
    {
      inputName: FORM_BUYER_OUTDOOR_ACCESS,
      questionName: QUIZ_QUESTION_BUYER_OUTDOOR,
      Component: QuizOutdoorQuestion,
      otherProps: commonProps
    },
    {
      inputName: FORM_BUYER_PET_EXPERIENCE,
      questionName: QUIZ_QUESTION_BUYER_PET_EXPERIENCE,
      Component: QuizPetExperienceQuestion,
      otherProps: commonProps
    },
    {
      inputName: FORM_BUYER_PROFILE_PHOTO,
      questionName: QUIZ_QUESTION_BUYER_PROFILE_PHOTO,
      Component: QuizProfilePhotoQuestion,
      otherProps: commonProps
    },
    {
      inputName: FORM_BUYER_LIFESTYLE,
      questionName: QUIZ_QUESTION_BUYER_LIFESTYLE,
      Component: QuizLifestyleQuestion,
      otherProps: commonProps
    }
  ].filter(step => !step.shouldSkip);

  const lastStep =
    orderedSteps.length > 0 ? orderedSteps[orderedSteps.length - 1] : {};

  function handleNextClick(stepIndex) {
    const previousStep = orderedSteps[stepIndex - 1];

    if (previousStep.inputName) {
      trackStepAnswer({
        step: previousStep,
        formState,
        properties: profileIds
      });
    }
  }

  lastStep.otherProps = {
    ...lastStep.otherProps,
    handleNextClick: null,
    onSubmit: handleSubmission,
    submitBtnLabel: isExpMiniAppEngagementVariantEnabled
      ? BUTTON_CONTINUE_LABEL
      : BUTTON_SEND_REQUEST_LABEL,
    submitBtnClassName:
      lastStep.otherProps?.variant === WIDE_QUIZ_QUESTION ? null : 'w-100',
    autoNext: false
  };

  function handleStepShown(index) {
    const { questionName } = orderedSteps[index];
    trackStepView({ questionName, ...profileIds });
  }

  return (
    <FormContext.Provider
      value={{
        ...contextValue,
        handleClose: closeModal
      }}
    >
      <OverlayModal
        className="bg-white"
        hideCloseButton
        closeButtonLabel="Exit"
        onClose={closeModal}
      >
        <LoadingWrapper className="bg-white h-100" isLoading={isLoading}>
          <FullPageQuiz
            className="bg-white z-max"
            formErrors={contextValue.formErrors}
            formState={contextValue.formState}
            handleFormFieldChange={handleFormFieldChange}
            isFormSubmitting={contextValue.isSubmitting}
            closeButtonLabel="Exit"
            orderedSteps={orderedSteps}
            onSaveAndExitClick={closeModal}
            onNextClick={handleNextClick}
            onSubmit={handleSubmission}
            isLoading={isLoading}
            onStepShown={handleStepShown}
            progressBarVariant={PROGRESS_BAR_VARIANTS.REGULAR}
            newDesignSystemStyles
          />
        </LoadingWrapper>
      </OverlayModal>
    </FormContext.Provider>
  );
}

export default function BuyerProfileQuiz(props) {
  return (
    <BuyerProfileContext.Provider
      value={{
        onClose: props.onClose,
        breederProfileId: props.breederProfileId,
        conversationId: props.conversationId,
        puppyName: props.puppyName
      }}
    >
      <BuyerProfileQuizContent />
    </BuyerProfileContext.Provider>
  );
}

BuyerProfileQuiz.propTypes = {
  onClose: PropTypes.func,
  breederProfileId: PropTypes.number,
  conversationId: PropTypes.number,
  puppyName: PropTypes.string
};

BuyerProfileQuiz.defaultProps = {
  onClose: null,
  breederProfileId: null,
  conversationId: null,
  puppyName: null
};
